/* MODULE B — Announcements & Notices: shared, pure notice logic.
   All India-facing time logic runs in IST (+05:30). No DOM, no DB — safe to
   import on the server, the client, and in tests. */

export const IST_OFFSET_MIN = 330 // +05:30

const H = 3_600_000

/** A time-critical notice stays in the PRIORITY lane until its event instant
    +12h, remains LISTED until +48h, then ARCHIVES out of the default list.
    Notices with no event instant are always 'listed' (they rely on ends_at). */
export type NoticeStage = 'priority' | 'listed' | 'archived'

export function noticeStage(eventAt: Date | string | null | undefined, now: Date = new Date()): NoticeStage {
  if (!eventAt) return 'listed'
  const e = typeof eventAt === 'string' ? new Date(eventAt) : eventAt
  if (Number.isNaN(e.getTime())) return 'listed'
  const elapsed = now.getTime() - e.getTime()
  if (elapsed < 12 * H) return 'priority' // upcoming, or within 12h after
  if (elapsed < 48 * H) return 'listed'
  return 'archived'
}

/** Interpret a wall-clock 'YYYY-MM-DDTHH:mm' (an HTML datetime-local value,
    entered by a committee in India) AS IST, returning the absolute instant. */
export function istLocalToInstant(wallClock: string | null | undefined): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(String(wallClock || ''))
  if (!m) return null
  const [y, mo, d, h, mi] = [Number(m[1]), Number(m[2]), Number(m[3]), Number(m[4]), Number(m[5])]
  const utcMs = Date.UTC(y, mo - 1, d, h, mi) - IST_OFFSET_MIN * 60_000
  const dt = new Date(utcMs)
  return Number.isNaN(dt.getTime()) ? null : dt
}

/** Format an instant in IST for display, e.g. "Fri 13 Jun, 5:30 PM". */
export function formatIst(input: Date | string | null | undefined): string {
  if (!input) return ''
  const d = typeof input === 'string' ? new Date(input) : input
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    weekday: 'short', day: '2-digit', month: 'short',
    hour: 'numeric', minute: '2-digit', hour12: true,
  })
}

/** Convert an instant to the IST wall-clock string an <input type="datetime-local">
    expects ('YYYY-MM-DDTHH:mm'), so edit forms round-trip in IST. */
export function instantToIstLocal(input: Date | string | null | undefined): string {
  if (!input) return ''
  const d = typeof input === 'string' ? new Date(input) : input
  if (Number.isNaN(d.getTime())) return ''
  const ist = new Date(d.getTime() + IST_OFFSET_MIN * 60_000)
  return ist.toISOString().slice(0, 16)
}

/** Validate a pasted Google Maps link, or build a directions URL from
    coordinates. Returns a safe https directions URL, or null when neither a
    recognised maps link nor valid coordinates are supplied. */
export function directionsUrl(input: {
  mapUrl?: string | null
  lat?: number | string | null
  lng?: number | string | null
}): string | null {
  const lat = input.lat == null || input.lat === '' ? NaN : Number(input.lat)
  const lng = input.lng == null || input.lng === '' ? NaN : Number(input.lng)
  if (Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
    return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
  }
  const u = String(input.mapUrl || '').trim()
  if (/^https:\/\/(www\.)?(google\.[a-z.]+\/maps|maps\.google\.[a-z.]+|maps\.app\.goo\.gl|goo\.gl\/maps)(\/|\?)/i.test(u)) {
    return u
  }
  return null
}

/** Build a WhatsApp share deep link for a notice (works on web + mobile). */
export function whatsappShareUrl(text: string): string {
  return `https://wa.me/?text=${encodeURIComponent(text)}`
}

/** Compose the plain-text share body for a notice, including structured
    janaza/event facts when present. Kept respectful and factual. */
export function noticeShareText(n: {
  title: string
  category?: string
  masjidName?: string
  body?: string
  eventAt?: string | Date | null
  meta?: NoticeMeta | null
  url?: string
}): string {
  const lines: string[] = []
  const cat = (n.category || '').toLowerCase()
  lines.push(n.title)
  if (cat === 'janaza' && n.meta?.deceasedName) {
    const age = n.meta.deceasedAge ? ` (${n.meta.deceasedAge})` : ''
    lines.push(`Marhoom: ${n.meta.deceasedName}${age}`)
    if (n.eventAt) lines.push(`Janaza namaz: ${formatIst(n.eventAt)}${n.meta.janazaMasjid ? ` · ${n.meta.janazaMasjid}` : ''}`)
    if (n.meta.burialGround) lines.push(`Burial: ${n.meta.burialGround}`)
  } else if (cat === 'event' && n.eventAt) {
    lines.push(`${formatIst(n.eventAt)}${n.meta?.eventVenue ? ` · ${n.meta.eventVenue}` : ''}`)
  } else if (n.body) {
    lines.push(n.body.slice(0, 220))
  }
  if (n.masjidName) lines.push(`— ${n.masjidName}`)
  if (n.url) lines.push(n.url)
  return lines.join('\n')
}

export interface NoticeMeta {
  // janaza
  deceasedName?: string | null
  deceasedAge?: number | null
  janazaMasjid?: string | null
  burialGround?: string | null
  burialMapUrl?: string | null
  burialLat?: number | null
  burialLng?: number | null
  // event
  eventVenue?: string | null
  eventMapUrl?: string | null
}
